import React, { useState } from 'react';
import { Cookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  ButtonIcon,
  ButtonSelect,
  Chip,
  Icon,
  InputField,
  Link as DSLink,
  Loader,
  TabGroup,
  TabItem,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  ALERT_BANNER_DEFAULT,
  CF_INF_STL,
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_PRI_MED,
  CF_SEC_MED,
} from '../../../shared/constants';
import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import { globalActions } from '../../../shared/store/globalSlice';
import { type RootState } from '../../../shared/types';
import { type AlertBannerState } from '../../../shared/types/globalTypes';
import { formatDisply } from '../../../shared/utils';
import AuditLogViewerSidePanelHistory from '../AuditLogViewerSidePanelHistory/AuditLogViewerSidePanelHistory';
import {
  useLazyGetAuditLogReviewsQuery,
  usePostAuditLogReviewMutation,
} from '../data-layer/auditLogApi';
import { type ReviewActivity } from '../data-layer/auditLogTypes';
import { auditLogActions, selectReviewActivitiesByLogId } from '../data-store';
import { useApiCancellation, useDateFormat } from '../hooks';
import type { TimelineData } from '../TimeLineBlock/types';
import { dispatchActivityLogMutationFailureBanner } from '../utils/dispatchActivityLogMutationFailureBanner';
import {
  formatChangeIndicatorLabel,
  formatDateByGlobal,
  formatEntitlementSeverityLabel,
  formatInTimeZoneWithDisplayFormat,
  getDisplayFormatForGlobalDateFormat,
} from '../utils/utils';

import './AuditLogDetailPanel.css';

const DASH_PLACEHOLDER = '-';

export interface AuditLogDetailPanelProps {
  /** Additional CSS class name */
  className?: string;
  /** Timeline item data to display */
  item: TimelineData;
  /** Callback when panel is closed */
  onClose?: () => void;
  /** Called before posting an action to capture parent state (e.g., list signature) */
  onPrepareAdvance?: (activeTab: 'summary' | 'review-activity') => void;
  /** Called after a successful action and banner display to advance to next item */
  onAdvanceToNextItem?: (activeTab: 'summary' | 'review-activity') => void;
  /** Which tab should be active by default when the panel opens or item changes */
  defaultActiveTab?: 'summary' | 'review-activity';
  /** Handler invoked when clicking the "Log Start Time (UTC)" value */
  onUtcLogStartTimeClick?: () => void;
  /** Handler invoked when "View Recording" link is clicked */
  onViewRecording?: () => void;
}

/**
 * AuditLogDetailPanel Component
 * Right-side detail panel that displays comprehensive information about a selected audit log item.
 * Shows event details, metadata, and related information as per Figma design.
 */
function AuditLogDetailPanel({
  className,
  item,
  onClose,
  onPrepareAdvance,
  onAdvanceToNextItem,
  defaultActiveTab = 'summary',
  onUtcLogStartTimeClick,
  onViewRecording,
}: AuditLogDetailPanelProps): React.ReactElement {
  const l = useInvertedLocalize();
  const [activeTab, setActiveTab] = useState<string>(defaultActiveTab);
  const [isFlagOpen, setIsFlagOpen] = useState(false);
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  // Global alert banner will be used; no local banner state

  const dispatch = useDispatch();
  const [
    triggerGetReviews,
    { isLoading: isReviewsLoading, isFetching: isReviewsFetching },
  ] = useLazyGetAuditLogReviewsQuery();
  const [postReview] = usePostAuditLogReviewMutation();
  const { registerAbort, cancelRequest } = useApiCancellation();
  const cookies = new Cookies();
  const userKey: string = cookies.get<string>('userKey') || '';

  // Get requestMetadata from Redux
  const requestMetadata = useTypedSelector(
    (s) => s.aag.auditLog.requestMetadata
  );

  const globalDateFormat = useDateFormat();
  const displayPattern = getDisplayFormatForGlobalDateFormat(globalDateFormat);

  // Removed mock reviews generator; rely solely on API results

  // Compute sodKey for fetching review activities from Redux
  const sodKey = String(item.uniqueLogId);

  // Load review activities from Redux for this sodKey
  const reduxReviewActivities = useTypedSelector((state: RootState) =>
    // Cast selector to accept RootState without narrowing the slice
    (
      selectReviewActivitiesByLogId as unknown as (
        s: RootState,
        k: string
      ) => ReviewActivity[]
    )(state, sodKey)
  );

  const hasReviewActivity = Boolean(item.latestActivity);

  const isDebouncingAdvanceRef = React.useRef(false);

  const onSaveClick = async (kind: 'flag' | 'comment'): Promise<void> => {
    try {
      // Notify parent to capture current list signature and active tab
      if (onPrepareAdvance) {
        onPrepareAdvance(activeTab as 'summary' | 'review-activity');
      }

      dispatch(auditLogActions.setIsPostingReview(true));
      let statusToSend: 'FLAGGED' | 'RESOLVED' | 'COMMENTED' = 'COMMENTED';

      if (kind === 'flag') {
        statusToSend = isFlagged ? 'RESOLVED' : 'FLAGGED';
      } else {
        statusToSend = 'COMMENTED';
      }

      const res = (await postReview({
        uniqueLogId: item.uniqueLogId,
        requestAccessKey: requestMetadata?.requestAccessKey ?? '',
        body: {
          comment: commentInput,
          status: statusToSend,
          actionedBy: userKey,
        },
      }).unwrap()) as { reviewActivities: ReviewActivity[] };

      dispatch(
        auditLogActions.setReviewActivities({
          uniqueLogId: sodKey,
          activities: res.reviewActivities as never[],
        })
      );

      // Update comment count badge on the timeline item
      const commentedCount = Array.isArray(res.reviewActivities)
        ? res.reviewActivities.filter((a) => a.status === 'COMMENTED').length
        : 0;

      dispatch(
        auditLogActions.updateTimelineItemCommentCount({
          uniqueLogId: String(item.uniqueLogId),
          commentsCount: commentedCount,
        })
      );

      if (kind === 'flag') {
        dispatch(
          auditLogActions.setTimelineItemStatus({
            uniqueLogId: item.uniqueLogId,
            rowLogStatus: statusToSend,
          })
        );
        setIsFlagOpen(false);
      } else {
        setIsCommentOpen(false);
      }

      dispatch(auditLogActions.setIsPostingReview(false));
      dispatch(auditLogActions.setPostReviewError(null));
      setCommentInput('');

      // Recompute comment count from full history: refetch review activities for this item
      if (requestMetadata?.requestAccessKey) {
        try {
          const refetchRes = await triggerGetReviews({
            uniqueLogId: sodKey,
            requestAccessKey: requestMetadata.requestAccessKey,
          }).unwrap();

          const refetchActivities = Array.isArray(
            (refetchRes as { reviewActivities?: unknown }).reviewActivities
          )
            ? ((refetchRes as { reviewActivities?: unknown })
                .reviewActivities as unknown[])
            : [];

          dispatch(
            auditLogActions.setReviewActivities({
              uniqueLogId: sodKey,
              activities: refetchActivities as never[],
            })
          );

          const reconciledCount = refetchActivities.filter(
            (a) => (a as { status?: string }).status === 'COMMENTED'
          ).length;

          dispatch(
            auditLogActions.updateTimelineItemCommentCount({
              uniqueLogId: String(item.uniqueLogId),
              commentsCount: reconciledCount,
            })
          );
        } catch {
          // best-effort: keep POST-derived state if refetch fails
        }
      }

      // Success banner + maintain current tab; advance will be handled by parent
      const successTitle = l('Success');
      let successDesc = l('Review comment added successfully');

      if (statusToSend === 'FLAGGED') {
        successDesc = l('Log item flagged successfully for review');
      } else if (statusToSend === 'RESOLVED') {
        successDesc = l('Log item reviewed and resolved successfully');
      }

      const banner: AlertBannerState = {
        ...ALERT_BANNER_DEFAULT,
        kind: 'Success',
        title: successTitle,
        description: successDesc,
        isOpen: true,
      } as AlertBannerState;

      dispatch(globalActions.setGlobalAlertBanner(banner));
      window.setTimeout(() => {
        dispatch(
          globalActions.setGlobalAlertBanner({
            ...ALERT_BANNER_DEFAULT,
            isOpen: false,
          } as AlertBannerState)
        );

        // Debounce to avoid duplicate advances in StrictMode/dev
        if (!isDebouncingAdvanceRef.current && onAdvanceToNextItem) {
          isDebouncingAdvanceRef.current = true;
          onAdvanceToNextItem(activeTab as 'summary' | 'review-activity');
          window.setTimeout(() => {
            isDebouncingAdvanceRef.current = false;
          }, 300);
        }
      }, 3000);
    } catch (e) {
      dispatch(auditLogActions.setIsPostingReview(false));
      let errMsg = l('Failed to add comment');

      if (kind === 'flag') {
        errMsg = l('Failed to post review');
      }

      if (
        e &&
        typeof e === 'object' &&
        'message' in (e as Record<string, unknown>)
      ) {
        errMsg = String((e as Record<string, unknown>).message);
      }

      dispatch(auditLogActions.setPostReviewError(errMsg));
      dispatchActivityLogMutationFailureBanner(dispatch, e, l);
    }
  };

  // Close any open drawers when the selected item changes (or panel re-opens)
  React.useEffect(() => {
    setIsFlagOpen(false);
    setIsCommentOpen(false);
    setCommentInput('');

    const nextDefaultTab = hasReviewActivity ? defaultActiveTab : 'summary';

    setActiveTab(nextDefaultTab);
  }, [item, defaultActiveTab, hasReviewActivity]);

  // Fetch review activities when the panel opens to populate the badge count
  React.useEffect(() => {
    if (
      requestMetadata?.requestAccessKey &&
      hasReviewActivity &&
      reduxReviewActivities.length === 0
    ) {
      // Cancel previous reviews request if any
      cancelRequest('getAuditLogReviews');

      const promise = triggerGetReviews({
        uniqueLogId: sodKey,
        requestAccessKey: requestMetadata.requestAccessKey,
      });

      // Register abort function for this request
      const abortablePromise = registerAbort(
        promise as unknown as ReturnType<typeof triggerGetReviews> & {
          abort?: () => void;
        },
        'getAuditLogReviews'
      );

      abortablePromise
        .unwrap()
        .then((res: unknown) => {
          const activities = Array.isArray(
            (res as { reviewActivities?: unknown }).reviewActivities
          )
            ? ((res as { reviewActivities?: unknown })
                .reviewActivities as unknown[])
            : [];

          dispatch(
            auditLogActions.setReviewActivities({
              uniqueLogId: sodKey,
              activities: activities as never[],
            })
          );
        })
        .catch(() => {
          // Silently fail - badge will just not show
        });
    }
  }, [
    sodKey,
    requestMetadata?.requestAccessKey,
    hasReviewActivity,
    triggerGetReviews,
    dispatch,
    reduxReviewActivities.length,
    registerAbort,
    cancelRequest,
  ]);

  // Fetch review activities when the Review Activity tab is opened
  React.useEffect(() => {
    if (activeTab === 'review-activity' && requestMetadata?.requestAccessKey) {
      // Cancel previous reviews request if any
      cancelRequest('getAuditLogReviews');

      const promise = triggerGetReviews({
        uniqueLogId: sodKey,
        requestAccessKey: requestMetadata.requestAccessKey,
      });

      // Register abort function for this request
      const abortablePromise = registerAbort(
        promise as unknown as ReturnType<typeof triggerGetReviews> & {
          abort?: () => void;
        },
        'getAuditLogReviews'
      );

      abortablePromise
        .unwrap()
        .then((res: unknown) => {
          const activities = Array.isArray(
            (res as { reviewActivities?: unknown }).reviewActivities
          )
            ? ((res as { reviewActivities?: unknown })
                .reviewActivities as unknown[])
            : [];

          dispatch(
            auditLogActions.setReviewActivities({
              uniqueLogId: sodKey,
              activities: activities as never[],
            })
          );
        })
        .catch(() => {
          // keep previous activities on error
        });
    }
  }, [
    activeTab,
    sodKey,
    triggerGetReviews,
    dispatch,
    requestMetadata?.requestAccessKey,
    registerAbort,
    cancelRequest,
  ]);

  const displayLogType = React.useMemo(() => {
    const type = item.logType ? item.logType.toUpperCase() : '';

    if (type.includes('CHANGE')) {
      return l('Change Log');
    }

    if (type.includes('T-CODE')) {
      return l('T-Code Log');
    }

    if (type.includes('SECURITY')) {
      return l('Security Audit Log');
    }

    if (type.includes('SYSTEM')) {
      return l('System Log');
    }

    return '';
  }, [item.logType, l]);

  const isChangeLog = React.useMemo(
    () => displayLogType === l('Change Log'),
    [displayLogType, l]
  );

  const isTCodeLog = React.useMemo(
    () => displayLogType === l('T-Code Log'),
    [displayLogType, l]
  );

  const isSystemLog = React.useMemo(
    () => displayLogType === l('System Log'),
    [displayLogType, l]
  );

  // Compute severity chip props
  const severityChipProps = React.useMemo(() => {
    if (!item.severity) {
      return null;
    }

    const sev = String(item.severity).toUpperCase();
    let severityLabel: string;
    let chipColor: 'success' | 'warning' | 'critical';

    if (sev === 'CRITICAL' || sev === 'HIGH') {
      chipColor = 'critical';
      severityLabel = l('High');
    } else if (sev === 'MEDIUM') {
      chipColor = 'warning';
      severityLabel = l('Medium');
    } else {
      chipColor = 'success';
      severityLabel = l('Low');
    }

    return { label: severityLabel, color: chipColor };
  }, [item.severity, l]);

  const isReadOnly = useTypedSelector((s) => s.aag.auditLog.isReadOnly);
  const isPostingReview = useTypedSelector(
    (s) => s.aag.auditLog.isPostingReview
  );

  // Keep handlers distinct: each UI element calls only its own callback.
  const handleViewRecordingClick = onViewRecording;
  const handleUtcLogStartTimeClick = onUtcLogStartTimeClick;

  const renderViewRecordingButton = (): React.ReactElement | null => {
    if (!handleViewRecordingClick) {
      return null;
    }

    return (
      <button
        type='button'
        className='Aag-AuditLogDetailPanel-recordingLink'
        onClick={() => handleViewRecordingClick()}>
        <Typography lineLimit={3} kind='body2-bold' color={CF_PRI_MED}>
          {l('View Recording')}
        </Typography>
        <Icon kind='ArrowRight' size='small' color={CF_PRI_MED} />
      </button>
    );
  };

  const renderSourceTimeField = (): React.ReactElement => (
    <Box className='Aag-AuditLogDetailPanel-field'>
      <Box className='Aag-AuditLogDetailPanel-fieldLabelWithIcon'>
        <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
          {l('Log Start Time (Source Time)')}
        </Typography>
        <Tooltip
          text={l(
            'This reflects the original time and time zone of the system that generated the log.'
          )}
          placement='left'
          trigger={
            <Icon kind='AlertInfoFilled' size='small' color={CF_INF_STL} />
          }
        />
      </Box>
      <Typography kind='body2' color={CF_SEC_MED}>
        {item.timeZone
          ? `${formatInTimeZoneWithDisplayFormat(
              item.logCreatedAt,
              item.timeZone,
              displayPattern
            )} (${item.timeZone})`
          : l('-')}
      </Typography>
    </Box>
  );

  const renderTimeSection = (): React.ReactElement => (
    <Box className='Aag-AuditLogDetailPanel-section'>
      <Box className='Aag-AuditLogDetailPanel-section-header'>
        <Typography lineLimit={3} kind='body2-bold' color={CF_NEU_BLD}>
          {l('Time')}
        </Typography>
      </Box>
      <Box className='Aag-AuditLogDetailPanel-section-content'>
        <Box className='Aag-AuditLogDetailPanel-timeSection'>
          <Box className='Aag-AuditLogDetailPanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Log Start Time (UTC)')}
            </Typography>
            <Box className='Aag-AuditLogDetailPanel-timeValue'>
              {handleUtcLogStartTimeClick ? (
                <button
                  type='button'
                  className='Aag-AuditLogDetailPanel-timeValueButton'
                  onClick={() => handleUtcLogStartTimeClick()}>
                  <Typography kind='body2' color={CF_SEC_MED}>
                    {formatInTimeZoneWithDisplayFormat(
                      item.logCreatedAt,
                      'UTC',
                      displayPattern
                    )}
                  </Typography>
                </button>
              ) : (
                <Typography kind='body2' color={CF_SEC_MED}>
                  {formatInTimeZoneWithDisplayFormat(
                    item.logCreatedAt,
                    'UTC',
                    displayPattern
                  )}
                </Typography>
              )}
              {renderViewRecordingButton()}
            </Box>
          </Box>
          {renderSourceTimeField()}
        </Box>
      </Box>
    </Box>
  );

  const renderOverviewSection = (): React.ReactElement => (
    <Box className='Aag-AuditLogDetailPanel-section'>
      <Box className='Aag-AuditLogDetailPanel-section-header'>
        <Typography lineLimit={3} kind='body2-bold' color={CF_NEU_BLD}>
          {l('Overview')}
        </Typography>
      </Box>
      <Box className='Aag-AuditLogDetailPanel-section-content'>
        <Box className='Aag-AuditLogDetailPanel-field'>
          <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
            {l('Log Type')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_MED}>
            {formatDisply(displayLogType) ?? '-'}
          </Typography>
        </Box>
        <Box className='Aag-AuditLogDetailPanel-field'>
          <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
            {l('Entitlement')}
          </Typography>
          {item.entitlementDisplayName ? (
            <Chip
              kind='outlined'
              size='small'
              label={
                formatDisply(item.entitlementDisplayName) ?? DASH_PLACEHOLDER
              }
              color='neutral'
            />
          ) : (
            <Typography kind='body2' color={CF_SEC_MED}>
              {DASH_PLACEHOLDER}
            </Typography>
          )}
        </Box>
        {(isChangeLog || isTCodeLog) && (
          <Box className='Aag-AuditLogDetailPanel-field'>
            <Box className='Aag-AuditLogDetailPanel-fieldLabelWithIcon'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Entitlement Deviate?')}
              </Typography>
              <Tooltip
                text={l(
                  'Indicates whether the T-Code executed in this session was part of the approved request.'
                )}
                placement='top'
                trigger={
                  <Icon
                    kind='AlertInfoFilled'
                    size='small'
                    color={CF_INF_STL}
                  />
                }
              />
            </Box>
            <Chip
              kind='outlined'
              size='xSmall'
              color={item.entitlementDeviation ? 'critical' : 'success'}
              label={item.entitlementDeviation ? 'Yes' : 'No'}
            />
          </Box>
        )}
        <Box className='Aag-AuditLogDetailPanel-field'>
          <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
            {l('Entitlement Description')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_MED}>
            {formatDisply(item.entitlementDescription) ?? '-'}
          </Typography>
        </Box>
        <Box className='Aag-AuditLogDetailPanel-field'>
          <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
            {l('Entitlement Function Area')}
          </Typography>
          <Typography lineLimit={3} kind='body2' color={CF_SEC_MED}>
            {Array.isArray(item.functionNames) && item.functionNames.length
              ? item.functionNames.join(', ')
              : l('-')}
          </Typography>
        </Box>
        <Box className='Aag-AuditLogDetailPanel-field'>
          <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
            {l('SOX Critical')}
          </Typography>
          <Typography lineLimit={3} kind='body2' color={CF_SEC_MED}>
            {formatEntitlementSeverityLabel(item.entitlementSoxSeverity, l)}
          </Typography>
        </Box>
        <Box className='Aag-AuditLogDetailPanel-field'>
          <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
            {l('Sys Critical')}
          </Typography>
          <Typography lineLimit={3} kind='body2' color={CF_SEC_MED}>
            {formatEntitlementSeverityLabel(item.entitlementSysSeverity, l)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderDetailsSection = (): React.ReactElement => {
    if (isChangeLog) {
      return (
        <Box className='Aag-AuditLogDetailPanel-section'>
          <Box className='Aag-AuditLogDetailPanel-section-header'>
            <Typography lineLimit={3} kind='body2-bold' color={CF_NEU_BLD}>
              {l('Change Log Details')}
            </Typography>
          </Box>
          <Box className='Aag-AuditLogDetailPanel-section-content'>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Object')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.object) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Doc Number')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.docNumber) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Table')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.table) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Table Key')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.tableKey) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Change Indicator')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatChangeIndicatorLabel(item.changeIndicator)}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Field')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.field) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Short Text')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.shortText) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Old Value')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.oldValue) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('New Value')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.newValue) ?? '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    if (isTCodeLog) {
      return (
        <Box className='Aag-AuditLogDetailPanel-section'>
          <Box className='Aag-AuditLogDetailPanel-section-header'>
            <Typography lineLimit={3} kind='body2-bold' color={CF_NEU_BLD}>
              {l('Entitlement Log Details')}
            </Typography>
          </Box>
          <Box className='Aag-AuditLogDetailPanel-section-content'>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Usage Period')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.usagePeriod) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Usage Value')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.usageValue) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Usage Start Date')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {item.usageStartDate
                  ? formatDateByGlobal(item.usageStartDate, globalDateFormat)
                  : '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Usage End Date')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {item.usageEndDate
                  ? formatDateByGlobal(item.usageEndDate, globalDateFormat)
                  : '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Instance')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.instance) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Client')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.client) ?? '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    if (isSystemLog) {
      return (
        <Box className='Aag-AuditLogDetailPanel-section'>
          <Box className='Aag-AuditLogDetailPanel-section-header'>
            <Typography lineLimit={3} kind='body2-bold' color={CF_NEU_BLD}>
              {l('System Log Details')}
            </Typography>
          </Box>
          <Box className='Aag-AuditLogDetailPanel-section-content'>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Severity')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.severity) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Detail')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.details, item.rowHeaderName) ?? '-'}
              </Typography>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-field'>
              <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
                {l('Client')}
              </Typography>
              <Typography kind='body2' color={CF_SEC_MED}>
                {formatDisply(item.client) ?? '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box className='Aag-AuditLogDetailPanel-section'>
        <Box className='Aag-AuditLogDetailPanel-section-header'>
          <Typography lineLimit={3} kind='body2-bold' color={CF_NEU_BLD}>
            {l('Audit Log Details')}
          </Typography>
        </Box>
        <Box className='Aag-AuditLogDetailPanel-section-content'>
          <Box className='Aag-AuditLogDetailPanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Severity')}
            </Typography>
            {severityChipProps ? (
              <Chip
                kind='subtleFilled'
                size='small'
                label={severityChipProps.label}
                color={severityChipProps.color}
              />
            ) : (
              <Typography kind='body2' color={CF_SEC_MED}>
                {DASH_PLACEHOLDER}
              </Typography>
            )}
          </Box>
          <Box className='Aag-AuditLogDetailPanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Audit Class')}
            </Typography>
            <Typography kind='body2' color={CF_SEC_MED}>
              {formatDisply(item.auditClass) ?? '-'}
            </Typography>
          </Box>
          <Box className='Aag-AuditLogDetailPanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Instance')}
            </Typography>
            <Typography kind='body2' color={CF_SEC_MED}>
              {formatDisply(item.instance) ?? '-'}
            </Typography>
          </Box>
          <Box className='Aag-AuditLogDetailPanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Terminal')}
            </Typography>
            <Typography kind='body2' color={CF_SEC_MED}>
              {formatDisply(item.terminal) ?? '-'}
            </Typography>
          </Box>
          <Box className='Aag-AuditLogDetailPanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Program Name')}
            </Typography>
            <Typography kind='body2' color={CF_SEC_MED}>
              {formatDisply(item.programName) ?? '-'}
            </Typography>
          </Box>
          <Box className='Aag-AuditLogDetailPanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Client')}
            </Typography>
            <Typography kind='body2' color={CF_SEC_MED}>
              {formatDisply(item.client) ?? '-'}
            </Typography>
          </Box>
          <Box className='Aag-AuditLogDetailPanel-field'>
            <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
              {l('Details')}
            </Typography>
            <Typography kind='body2' color={CF_SEC_MED}>
              {formatDisply(item.details) ?? '-'}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderReportSection = (): React.ReactElement => (
    <Box className='Aag-AuditLogDetailPanel-section'>
      <Box className='Aag-AuditLogDetailPanel-section-content'>
        <Box className='Aag-AuditLogDetailPanel-field'>
          <Typography lineLimit={3} kind='body3' color={CF_NEU_STL}>
            {l('Report Creation Time')}
          </Typography>
          <Typography kind='body2' color={CF_SEC_MED}>
            {formatDateByGlobal(item.reportCreatedAt, globalDateFormat)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // Drawer titles/descriptions
  const isResolved = item.rowLogStatus === 'RESOLVED';
  const isFlagged = item.rowLogStatus === 'FLAGGED';
  const isCommented = item.rowLogStatus === 'COMMENTED';
  const flagDrawerTitle = isFlagged ? 'Resolve Flag' : 'Flag';

  // Render status chip based on rowLogStatus
  const renderStatusChip = (): React.ReactElement => {
    const statusConfig: Record<
      string,
      { color: 'warning' | 'success' | 'info'; label: string; icon: string }
    > = {
      FLAGGED: {
        color: 'warning',
        label: l('Flagged'),
        icon: 'FlagFilled',
      },
      RESOLVED: {
        color: 'success',
        label: l('Resolved'),
        icon: 'FlagResolved',
      },
      COMMENTED: {
        color: 'info',
        label: l('Commented ({count})', {
          count: item.commentsCount ?? 0,
        }),
        icon: 'Comment',
      },
    };

    const config = statusConfig[item.rowLogStatus];

    return (
      <Chip
        color={config.color}
        size='small'
        kind='boldFilled'
        label={config.label}
        leftIcon={
          <Icon
            kind={config.icon as 'FlagFilled' | 'FlagResolved' | 'Comment'}
          />
        }
      />
    );
  };

  const flagDrawerDescription = isFlagged
    ? 'To resolve this issue and remove the flag, provide a brief explanation of what was done to address it.'
    : 'Flag this item if it appears suspicious or requires further investigation before the review can be completed.';

  const renderActionContent = (
    kind: 'flag' | 'comment'
  ): React.ReactElement => (
    <Box className='Aag-AuditLogDetailPanel-actionContent'>
      <Box className='Aag-AuditLogDetailPanel-field'>
        <InputField
          name='flag'
          isRequired
          kind='multiline'
          value={commentInput}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setCommentInput(e.target.value)
          }
          placeholder={l('Add your comment')}
          label={
            <Typography lineLimit={3} kind='label'>
              {l('Comment')}
            </Typography>
          }
        />
      </Box>
      <Box className='Aag-AuditLogDetailPanel-actionContent-buttons'>
        <Button
          kind='outlined'
          size='small'
          onClick={() =>
            kind === 'flag' ? setIsFlagOpen(false) : setIsCommentOpen(false)
          }>
          {l('Cancel')}
        </Button>
        <Button
          kind='outlined'
          size='small'
          isDisabled={Boolean(
            isPostingReview || commentInput.trim().length <= 2
          )}
          onClick={async () => onSaveClick(kind)}>
          {isPostingReview ? 'Saving ...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );

  const renderDrawer = (
    kind: 'flag' | 'comment',
    title: string,
    description: string,
    isOpen: boolean,
    handleClose: () => void,
    headerIcon: 'Flag' | 'Comment'
  ): React.ReactElement | null => {
    if (!isOpen) {
      return null;
    }

    return (
      <Box className='Aag-AuditLogDetailPanel-actionDrawer'>
        <Box className='Aag-AuditLogDetailPanel-actionDrawer-main'>
          <Box className='Aag-AuditLogDetailPanel-drawerHeader'>
            <ButtonIcon
              size='small'
              kind='ghost'
              ariaLabel={l('close')}
              icon={<Icon kind={headerIcon} />}
              onClick={handleClose}
            />
            <Box className='Aag-AuditLogDetailPanel-drawerHeader-header-description'>
              <Box className='Aag-AuditLogDetailPanel-drawerHeader-header-frame'>
                <Typography lineLimit={3} kind='h4'>
                  {title}
                </Typography>
                <ButtonIcon
                  size='small'
                  kind='ghost'
                  ariaLabel={l('close')}
                  icon={<Icon kind='Close' />}
                  onClick={handleClose}
                />
              </Box>
              <Box className='Aag-AuditLogDetailPanel-drawerHeader-description-frame'>
                {description}
              </Box>
            </Box>
          </Box>
          <Box className='Aag-AuditLogDetailPanel-drawerContent'>
            {renderActionContent(kind)}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderReviewActivity = (): React.ReactElement | null => {
    if (!hasReviewActivity) {
      return null;
    }

    return (
      <Box className='Aag-AuditLogDetailPanel-section Aag-AuditLogDetailPanel-section-reviewActivity'>
        <Box className='Aag-AuditLogDetailPanel-section-header'>
          <Typography lineLimit={3} kind='body2-bold' color={CF_NEU_BLD}>
            {l('Review Activity')}
          </Typography>
        </Box>
        <Box className='Aag-AuditLogDetailPanel-section-reviewActivity'>
          <Box className='Aag-AuditLogDetailPanel-section-content'>
            <AuditLogViewerSidePanelHistory
              sodKey={sodKey}
              activities={
                item.latestActivity
                  ? [
                      {
                        status: item.latestActivity.status,
                        user: item.latestActivity.updatedBy ?? '-',
                        comment: item.latestActivity.comment ?? '',
                        updatedAt: item.latestActivity.updatedAt ?? '',
                      },
                    ]
                  : []
              }
            />
          </Box>
          <Box className='Aag-AuditLogDetailPanel-section-footer'>
            <DSLink
              text={l('View All')}
              color='brand'
              kind='button'
              onClick={() => setActiveTab('review-activity')}
              size='medium'
              className='Aag-AuditLogDetailPanel-section-footer-link'
            />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderReviewActivityTabContent = (): React.ReactElement => {
    if (isReviewsLoading || isReviewsFetching) {
      return (
        <Box className='Aag-AuditLogDetailPanel-loader'>
          <Loader kind='dots' format='inline' color='brand' size='small' />
        </Box>
      );
    }

    if (reduxReviewActivities.length === 0) {
      return (
        <Box className='Aag-AuditLogDetailPanel-noData'>
          <Typography kind='body2' color={CF_NEU_STL}>
            {l('No review activity yet.')}
          </Typography>
        </Box>
      );
    }

    return (
      <AuditLogViewerSidePanelHistory
        sodKey={sodKey}
        activities={reduxReviewActivities}
      />
    );
  };

  return (
    <Box className={cx('Aag-AuditLogDetailPanel', className)}>
      {/* Header */}
      <Box className='Aag-AuditLogDetailPanel-header'>
        {/* Unified Header for all contexts */}
        <Box className='Aag-AuditLogDetailPanel-header-content-regular'>
          <Box className='Aag-AuditLogDetailPanel-header-content'>
            <Box className='Aag-AuditLogDetailPanel-header-icon'>
              <Box className='Aag-AuditLogDetailPanel-appIcon'>
                <Icon kind='Task' size='medium' />
              </Box>
            </Box>
            <Box className='Aag-AuditLogDetailPanel-header-title'>
              <Typography kind='h2' color={CF_NEU_BLD} lineLimit={3}>
                {item.rowHeaderName}
              </Typography>
            </Box>
            {(isFlagged || isResolved || isCommented) && (
              <Box className='Aag-AuditLogDetailPanel-header-status'>
                {renderStatusChip()}
              </Box>
            )}
          </Box>
          <ButtonIcon
            size='medium'
            kind='ghost'
            ariaLabel={l('Close')}
            icon={<Icon kind='Close' size='medium' />}
            onClick={onClose}
          />
        </Box>
        <Box className='Aag-AuditLogDetailPanel-tabs'>
          <TabGroup value={activeTab} onChange={setActiveTab}>
            <TabItem
              value='summary'
              label={l('Summary')}
              icon={<Icon kind='Document' size='medium' />}
            />
            {hasReviewActivity && (
              <TabItem
                value='review-activity'
                label={l('Review Activity')}
                icon={<Icon kind='Time' size='medium' />}
                BadgeComp={
                  reduxReviewActivities.length > 0 ? (
                    <Badge
                      badgeStyle={
                        activeTab === 'review-activity' ? 'filled' : 'outlined'
                      }
                      color='secondary'
                      kind='number'
                      size='medium'
                      number={reduxReviewActivities.length}
                    />
                  ) : undefined
                }
              />
            )}
          </TabGroup>
        </Box>
      </Box>

      {/* Content */}
      <Box className='Aag-AuditLogDetailPanel-content'>
        {activeTab === 'review-activity' ? (
          <Box className='Aag-AuditLogDetailPanel-section'>
            <Box className='Aag-AuditLogDetailPanel-section-content'>
              {renderReviewActivityTabContent()}
            </Box>
          </Box>
        ) : (
          <>
            {renderReviewActivity()}
            {renderTimeSection()}
            {renderOverviewSection()}
            {renderDetailsSection()}
            {renderReportSection()}
          </>
        )}
      </Box>

      {/* Footer */}
      {!isReadOnly && activeTab !== 'review-activity' && (
        <Box className='Aag-AuditLogDetailPanel-footer'>
          <Box className='Aag-AuditLogDetailPanel-footer-actions'>
            {isResolved ? (
              <>
                <Box>
                  <ButtonSelect
                    prefixIcon={<Icon kind='Comment' color={CF_PRI_MED} />}
                    size='medium'
                    label={l('Comment')}
                    isVisualCueEnabled={false}
                    onClick={() => {
                      if (!isCommentOpen) {
                        setIsFlagOpen(false);
                      }

                      setIsCommentOpen(!isCommentOpen);
                    }}
                  />
                </Box>
                <Box>
                  <ButtonSelect
                    size='medium'
                    label={l('View all activities')}
                    isVisualCueEnabled={false}
                    onClick={() => {
                      setIsFlagOpen(false);
                      setIsCommentOpen(false);
                      setActiveTab('review-activity');
                    }}
                  />
                </Box>
              </>
            ) : (
              <>
                <Box>
                  {isFlagged ? (
                    <Button
                      kind='filled'
                      size='medium'
                      leftIcon={<Icon kind='FlagResolved' color='gray-000' />}
                      onClick={() => {
                        if (!isFlagOpen) {
                          setIsCommentOpen(false);
                        }

                        setIsFlagOpen(!isFlagOpen);
                      }}>
                      {l('Resolve Flag')}
                    </Button>
                  ) : (
                    <ButtonSelect
                      prefixIcon={<Icon kind='Flag' color={CF_PRI_MED} />}
                      size='medium'
                      label={l('Flag')}
                      isVisualCueEnabled={false}
                      onClick={() => {
                        if (!isFlagOpen) {
                          setIsCommentOpen(false);
                        }

                        setIsFlagOpen(!isFlagOpen);
                      }}
                    />
                  )}
                </Box>
                <Box>
                  <ButtonSelect
                    prefixIcon={<Icon kind='Comment' color={CF_PRI_MED} />}
                    size='medium'
                    label={l('Comment')}
                    isVisualCueEnabled={false}
                    onClick={() => {
                      if (!isCommentOpen) {
                        setIsFlagOpen(false);
                      }

                      setIsCommentOpen(!isCommentOpen);
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {renderDrawer(
        'flag',
        flagDrawerTitle,
        flagDrawerDescription,
        isFlagOpen,
        () => setIsFlagOpen(false),
        'Flag'
      )}
      {renderDrawer(
        'comment',
        'Add Comments',
        'Add a note to this log item without flagging it. Use this to document context, questions, or general observations during review.',
        isCommentOpen,
        () => setIsCommentOpen(false),
        'Comment'
      )}
    </Box>
  );
}

AuditLogDetailPanel.defaultProps = {
  className: undefined,
  onClose: undefined,
  onPrepareAdvance: undefined,
  onAdvanceToNextItem: undefined,
  defaultActiveTab: 'summary',
  onUtcLogStartTimeClick: undefined,
  onViewRecording: undefined,
};

export default AuditLogDetailPanel;
